import { createContext, useState, ReactNode, useMemo } from "react";
import { Form, StepItemProps, useToast } from "@ir/ui";
import {
  AddChargebreakdownContextType,
  IAddChargebreakdownHeaders,
  IImportedChargebreakdownFile,
  IAddChargebreakdownImportedFileData,
  IImportedChargebreakdownFileError,
} from "../interface/AddChargebreakdownInterface";
import { addChargebreakdownHeadersMasterRaw, addChargebreakdownStepsMasterRaw } from "../utils/AddChargebreakdownUtils";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl/GraphQl.adapter";
import { DeductionItemRepository } from "@/infrastructure/repositories/deduction";
import { AddDeductionItemUseCase } from "@/core/usecases/deduction/AddDeductionItem.useCase";
import { AddChargebreakdownItem } from "../class/AddChargebreakdownItem";

export const AddChargebreakdownContext = createContext<AddChargebreakdownContextType | undefined>(undefined);

interface AddChargebreakdownProviderProps {
  children: ReactNode;
  deductionId: number;
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
  customHeaders?: IAddChargebreakdownHeaders[];
  customSteps?: StepItemProps[];
}

const client = GraphQlAdapter.getInstance().getClient();
const deductionItemRepository = new DeductionItemRepository(client);

export const AddChargebreakdownProvider = ({
  children,
  deductionId,
  onSuccess,
  onError,
  onClose,
  customHeaders,
  customSteps,
}: AddChargebreakdownProviderProps) => {
  const { error: errorToast } = useToast();

  const { success: successToast } = useToast();

  // Modal Header Height
  const [modalHeaderHeight, setModalHeaderHeight] = useState<number>(0);

  // Modal Footer Height
  const [modalFooterHeight, setModalFooterHeight] = useState<number>(0);

  // Imported File
  const [importedFile, setImportedFile] = useState<IImportedChargebreakdownFile | null>(null);

  // Imported File Data
  const [importedFileData, setImportedFileData] = useState<IAddChargebreakdownImportedFileData | null>(null);

  // Imported File Error
  const [importedFileError, setImportedFileError] = useState<IImportedChargebreakdownFileError | null>(null);

  // Final chargebreakdown data
  const [finalChargebreakdownData, setFinalChargebreakdownData] = useState<AddChargebreakdownItem[] | null>(null);

  // App Headers Array
  const [appHeadersMaster] = useState<IAddChargebreakdownHeaders[]>(customHeaders || [...addChargebreakdownHeadersMasterRaw]);

  // Add Chargebreakdown Steps
  const [addChargebreakdownSteps] = useState<StepItemProps[]>(customSteps || [...addChargebreakdownStepsMasterRaw]);

  // Selected Steps
  const [selectedSteps, setSelectedSteps] = useState<string[]>(["upload-chargebreakdown"]);

  // Current Step
  const [currentStep, setCurrentStep] = useState<string>("upload-chargebreakdown");

  // Field Mapping Form
  const [fieldMappingForm] = Form.useForm();

  // Is Submitting
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Is submitted
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  // Is processing success response
  const [isProcessingSuccessResponse, setIsProcessingSuccessResponse] = useState<boolean>(false);

  // Success Response
  const [successResponse, setSuccessResponse] = useState<any>(null);

  // Error Response
  const [errorResponse, setErrorResponse] = useState<any>(null);

  // Get Final Chargebreakdown Data Count
  const getFinalChargebreakdownDataCount = () => {
    if (!finalChargebreakdownData || finalChargebreakdownData.length === 0) return 0;
    return finalChargebreakdownData.length;
  };

  // Get Final Chargebreakdown Data Errors Count
  const getFinalChargebreakdownDataErrorsCount = () => {
    if (!finalChargebreakdownData || finalChargebreakdownData.length === 0) return 0;
    return finalChargebreakdownData.filter(item => item.hasErrors).length;
  };

  const deleteChargebreakdownFromFinalChargebreakdownData = (chargebreakdownKey: string) => {
    if (!finalChargebreakdownData) return;
    const updatedFinalChargebreakdownData = finalChargebreakdownData.filter(item => item.key !== chargebreakdownKey);
    setFinalChargebreakdownData(updatedFinalChargebreakdownData.length > 0 ? updatedFinalChargebreakdownData : []);
  };

  // Transform chargebreakdown item to submission format
  const transformChargebreakdownItem = (item: AddChargebreakdownItem) => ({
    key: item.key,
    upcItemNumber: item.upcItemNumber ? item.upcItemNumber?.toString().trim() : null,
    itemNumber: item.itemNumber ? item.itemNumber?.toString().trim() : null,
    totalAmount: item.totalAmount ?? null,
    itemDescription: item.itemDescription ? item.itemDescription?.toString().trim() : null,
    distributionCenter: item.distributionCenter ? item.distributionCenter?.toString().trim() : null,
    orderQuantity: item.orderQuantity ? item.orderQuantity : null,
    quantity: item.quantity ? item.quantity : null,
    quantityDifference: item.quantityDifference ? item.quantityDifference : null,
    packSize: item.packSize ? item.packSize.toString().trim() : null,
    unitCost: item.unitCost ? item.unitCost : null,
    receivedUnitCost: item.receivedUnitCost ? item.receivedUnitCost : null,
    costDifference: item.costDifference ? item.costDifference : null,
    allowance: item.allowance ? item.allowance.toString().trim() : null,
    receivedDate: item.receivedDate ? item.receivedDate.toString().trim() : null,
    expiryDate: item.expiryDate ? item.expiryDate.toString().trim() : null,
    shipDate: item.shipDate ? item.shipDate.toString().trim() : null,
    poNumber: item.poNumber ? item.poNumber?.toString().trim() : null,
    poDate: item.poDate ? item.poDate.toString().trim() : null,
    retailerName: item.retailerName ? item.retailerName?.toString().trim() : null,
    retailerNumber: item.retailerNumber ? item.retailerNumber?.toString().trim() : null,
    refInvoice: item.refInvoice ? item.refInvoice?.toString().trim() : null,
    invoiceAmount: item.invoiceAmount ? item.invoiceAmount : null,
    invoiceDate: item.invoiceDate ? item.invoiceDate.toString().trim() : null,
    discount: item.discount ? item.discount.toString().trim() : null,
    divisionName: item.divisionName ? item.divisionName?.toString().trim() : null,
    divisionNumber: item.divisionNumber ? item.divisionNumber?.toString().trim() : null,
  });

  // Prepare chargebreakdown data for submission
  const prepareChargebreakdownDataForSubmission = () => {
    if (!finalChargebreakdownData || finalChargebreakdownData.length === 0) {
      return null;
    }

    const chargebreakdownDataForSubmission = finalChargebreakdownData.filter(item => item.hasErrors === false).map(transformChargebreakdownItem);

    if (chargebreakdownDataForSubmission.length === 0) {
      throw new Error("No valid chargebreakdown data to submit");
    }

    return chargebreakdownDataForSubmission;
  };

  // Handle successful submission
  const handleSuccessfulSubmission = (response: any) => {
    setSuccessResponse(response);
    setIsSubmitting(false);
    setIsSubmitted(true);

    if (onSuccess) {
      onSuccess(response);
      successToast({
        content: `${response.createDeductionItem.data.newlyInsertedDeductionItems.length} - item(s) added to the charge breakdown. `,
      });
    }

    setIsProcessingSuccessResponse(false);
  };

  // Handle submission error
  const handleSubmissionError = (error: any) => {
    errorToast({
      content: "Failed to submit chargebreakdown(s). Please try again.",
    });
    setErrorResponse(error);
    setIsSubmitting(false);
    setIsProcessingSuccessResponse(false);

    if (onError) {
      onError(error);
    }
  };

  // Submit Final Chargebreakdown Data
  const submitFinalChargebreakdownData = async () => {
    try {
      setIsSubmitting(true);

      const chargebreakdownDataForSubmission = prepareChargebreakdownDataForSubmission();
      if (!chargebreakdownDataForSubmission) return;

      const addChargebreakdownUseCase = new AddDeductionItemUseCase(deductionItemRepository);

      setIsProcessingSuccessResponse(true);
      const addChargebreakdownResponse = await addChargebreakdownUseCase.execute({
        deductionId,
        deductionItems: chargebreakdownDataForSubmission,
      });

      handleSuccessfulSubmission(addChargebreakdownResponse);
    } catch (error) {
      handleSubmissionError(error);
    }
  };

  // Get processed chargebreakdown success count
  const getProcessedChargebreakdownSuccessCount = () => {
    if (!successResponse?.createDeductionItem?.data) return 0;
    // Count newly inserted deduction items from response
    return successResponse.createDeductionItem.data.newlyInsertedDeductionItems.length;
  };

  // Get processed chargebreakdown error count
  const getProcessedChargebreakdownErrorCount = () => {
    if (!finalChargebreakdownData) return 0;
    return finalChargebreakdownData.filter(item => item.hasErrors).length;
  };

  // Clear all data
  const clearAllData = () => {
    setImportedFile(null);
    setImportedFileData(null);
    setImportedFileError(null);
    setFinalChargebreakdownData(null);
    setIsSubmitting(false);
    setIsSubmitted(false);
    setSuccessResponse(null);
    setErrorResponse(null);
    setIsProcessingSuccessResponse(false);
    const currentStepKey = "upload-chargebreakdown";
    setSelectedSteps([currentStepKey]);
    setCurrentStep(currentStepKey);
    fieldMappingForm.resetFields();
  };

  // Close modal and call onClose callback
  const closeModal = () => {
    clearAllData();
    if (onClose) {
      onClose();
    }
  };

  // Continue to next step
  const continueToNextStep = () => {
    if (currentStep === "upload-chargebreakdown") {
      setSelectedSteps(["upload-chargebreakdown", "map-chargebreakdown"]);
      setCurrentStep("map-chargebreakdown");
    } else if (currentStep === "map-chargebreakdown") {
      fieldMappingForm.submit();
    } else if (currentStep === "verify-chargebreakdown") {
      if (getFinalChargebreakdownDataErrorsCount() === 0 && getFinalChargebreakdownDataCount() > 0) {
        submitFinalChargebreakdownData();
      }
    }
  };

  // Continue to previous step
  const continueToPreviousStep = () => {
    if (currentStep === "map-chargebreakdown") {
      setSelectedSteps(["upload-chargebreakdown"]);
      setCurrentStep("upload-chargebreakdown");
    } else if (currentStep === "verify-chargebreakdown") {
      setSelectedSteps(["upload-chargebreakdown", "map-chargebreakdown"]);
      setCurrentStep("map-chargebreakdown");
    }
  };

  const contextValue = useMemo(
    () => ({
      // Modal Header Height
      modalHeaderHeight,
      setModalHeaderHeight,

      // Modal Footer Height
      modalFooterHeight,
      setModalFooterHeight,

      // Imported File
      importedFile,
      setImportedFile,

      // Imported File Data
      importedFileData,
      setImportedFileData,

      // Imported File Error
      importedFileError,
      setImportedFileError,

      // Final chargebreakdown data
      finalChargebreakdownData,
      setFinalChargebreakdownData,

      // Is Submitting
      isSubmitting,
      setIsSubmitting,

      // Is Submitted
      isSubmitted,
      setIsSubmitted,

      // Is Processing Success Response
      isProcessingSuccessResponse,
      setIsProcessingSuccessResponse,

      // Success Response
      successResponse,
      setSuccessResponse,

      // Error Response
      errorResponse,
      setErrorResponse,

      // Get Final Chargebreakdown Data Count
      getFinalChargebreakdownDataCount,

      // Delete chargebreakdown from final chargebreakdown data
      deleteChargebreakdownFromFinalChargebreakdownData,

      // Get Final Chargebreakdown Data Errors Count
      getFinalChargebreakdownDataErrorsCount,

      // Get Processed Chargebreakdown Success Count
      getProcessedChargebreakdownSuccessCount,

      // Get Processed Chargebreakdown Error Count
      getProcessedChargebreakdownErrorCount,

      // Get current deduction ID
      deductionId,

      // Add Chargebreakdown Steps
      addChargebreakdownSteps,

      // App Headers Master
      appHeadersMaster,

      // Selected Steps
      selectedSteps,
      setSelectedSteps,

      // Current Step
      currentStep,
      setCurrentStep,

      // Clear all data
      clearAllData,

      // Close modal
      closeModal,

      // Continue to next step
      continueToNextStep,

      // Continue to previous step
      continueToPreviousStep,

      // Field Mapping Form
      fieldMappingForm,
    }),
    [
      modalHeaderHeight,
      modalFooterHeight,
      importedFile,
      importedFileData,
      importedFileError,
      finalChargebreakdownData,
      selectedSteps,
      currentStep,
      addChargebreakdownSteps,
      appHeadersMaster,
      fieldMappingForm,
      isSubmitting,
      isSubmitted,
      isProcessingSuccessResponse,
      successResponse,
      errorResponse,
      deductionId,
    ]
  );

  return <AddChargebreakdownContext.Provider value={contextValue}>{children}</AddChargebreakdownContext.Provider>;
};
